import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../supabase';
import Navbar from '../components/Navbar';
import { toast } from 'sonner';
import {
  ClipboardList, CheckCircle2, LogOut, Menu, X, Pill, ChevronLeft
} from 'lucide-react';
import './PharmacyDashboard.css';

function PharmacyDashboard() {
  const navigate = useNavigate();
  const { logout, currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('active');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [pharmacyInfo, setPharmacyInfo] = useState(null);
  const [activePrescriptions, setActivePrescriptions] = useState([]);
  const [completedPrescriptions, setCompletedPrescriptions] = useState([]);
  const [selectedPrescription, setSelectedPrescription] = useState(null);
  const [medicinePrices, setMedicinePrices] = useState({});
  const [medicineUnavailable, setMedicineUnavailable] = useState({});
  const [medicineSubstitutes, setMedicineSubstitutes] = useState({});
  const [medicineQuantities, setMedicineQuantities] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSecondaryInputs, setShowSecondaryInputs] = useState({});
  const [cancelConfirmId, setCancelConfirmId] = useState(null);

  useEffect(() => {
    if (currentUser) fetchPharmacyInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  useEffect(() => {
    if (!pharmacyInfo) return;

    fetchPrescriptions();

    // 1. Supabase Realtime Subscription (Fires if replication is enabled natively)
    const subscription = supabase
      .channel('pharmacy_prescriptions')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'prescriptions' },
        (payload) => {
          fetchPrescriptions();
        }
      )
      .subscribe();

    // 2. Tab Focus trigger - lightweight safety net
    window.addEventListener('focus', fetchPrescriptions);

    return () => {
      supabase.removeChannel(subscription);
      window.removeEventListener('focus', fetchPrescriptions);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pharmacyInfo]);

  async function fetchPharmacyInfo() {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', currentUser.id)
        .single();
      if (error) throw error;
      setPharmacyInfo(data);
    } catch (error) {
      toast.error('Failed to load pharmacy profile');
    }
  }

  async function fetchPrescriptions() {
    try {
      const { data, error } = await supabase
        .from('prescriptions')
        .select('*')
        .eq('clinic_id', pharmacyInfo.clinic_id)
        .eq('sent_to_pharmacy', true)
        .order('created_at', { ascending: false });
      if (error) throw error;

      const prescriptions = data || [];
      setActivePrescriptions(prescriptions.filter(p => p.pharmacy_status === 'pending'));
      setCompletedPrescriptions(prescriptions.filter(p => p.pharmacy_status === 'dispensed'));
    } catch (error) {
      toast.error('Failed to load prescriptions');
    }
  }

  const filteredActivePrescriptions = activePrescriptions.filter(p =>
    p.patient_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.patient_phone?.includes(searchTerm)
  );

  const filteredCompletedPrescriptions = completedPrescriptions.filter(p =>
    p.patient_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.patient_phone?.includes(searchTerm)
  );

  function handleViewPrescription(prescription) {
    if (selectedPrescription?.id === prescription.id) {
      setSelectedPrescription(null); // Toggle off if clicking the same card
      return;
    }
    setSelectedPrescription(prescription);
    const prices = {};
    const unavail = {};
    const subs = {};
    const quants = {};
    (prescription.medicines || []).forEach((med, index) => {
      prices[index] = med.price || '';
      unavail[index] = med.unavailable || false;
      subs[index] = med.substituted_with || '';
      quants[index] = med.dispensed_quantity || '';
    });
    setMedicinePrices(prices);
    setMedicineUnavailable(unavail);
    setMedicineSubstitutes(subs);
    setMedicineQuantities(quants);
    setShowSecondaryInputs({});
    setCancelConfirmId(null);
  }

  function toggleSecondaryInputs(index) {
    setShowSecondaryInputs(prev => ({ ...prev, [index]: !prev[index] }));
  }

  function updateMedicinePrice(index, price) {
    setMedicinePrices(prev => ({ ...prev, [index]: price }));
  }

  function toggleMedicineUnavailable(index) {
    setMedicineUnavailable(prev => {
      const newVal = !prev[index];
      if (newVal) {
        // If marked unavailable, zero out the price
        setMedicinePrices(p => ({ ...p, [index]: 0 }));
      }
      return { ...prev, [index]: newVal };
    });
  }

  function updateMedicineSubstitute(index, sub) {
    setMedicineSubstitutes(prev => ({ ...prev, [index]: sub }));
  }

  function updateMedicineQuantity(index, qty) {
    setMedicineQuantities(prev => ({ ...prev, [index]: qty }));
  }

  // Calculate live total based on current prices state
  const liveTotal = (selectedPrescription?.medicines || []).reduce((sum, _, idx) => {
    return sum + (parseFloat(medicinePrices[idx]) || 0);
  }, 0);

  async function handleMarkAsCompleted() {
    if (!selectedPrescription) return;

    const allPricedOrUnavailable = (selectedPrescription.medicines || []).every((med, idx) =>
      medicineUnavailable[idx] || (medicinePrices[idx] !== '' && parseFloat(medicinePrices[idx]) >= 0)
    );
    if (!allPricedOrUnavailable) {
      toast.error('Please add prices for all available medicines before completing');
      return;
    }

    setLoading(true);
    try {
      const updatedMedicines = (selectedPrescription.medicines || []).map((med, idx) => ({
        ...med,
        price: parseFloat(medicinePrices[idx] || 0),
        unavailable: medicineUnavailable[idx] || false,
        substituted_with: medicineSubstitutes[idx] || '',
        dispensed_quantity: medicineQuantities[idx] || ''
      }));
      const totalAmount = updatedMedicines.reduce((sum, med) => sum + (med.price || 0), 0);

      let newStatus = selectedPrescription.status;
      if (selectedPrescription.lab_status === 'not_applicable' || selectedPrescription.lab_status === 'completed') {
        newStatus = 'completed';
      }

      const { error } = await supabase
        .from('prescriptions')
        .update({
          medicines: updatedMedicines,
          pharmacy_status: 'dispensed',
          status: newStatus,
          total_amount: totalAmount,
          dispensed_by: pharmacyInfo.name,
          dispensed_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedPrescription.id);

      if (error) throw error;

      toast.success('Prescription dispensed and receipt sent successfully');
      setSelectedPrescription(null);
      setMedicinePrices({});
      fetchPrescriptions();
    } catch (error) {
      toast.error(`Error updating prescription: ${error.message}`);
    }
    setLoading(false);
  }

  async function handleCancelPrescription() {
    if (!selectedPrescription) return;

    if (cancelConfirmId !== selectedPrescription.id) {
      setCancelConfirmId(selectedPrescription.id);
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase
        .from('prescriptions')
        .update({
          pharmacy_status: 'cancelled',
          status: 'cancelled',
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedPrescription.id);

      if (error) throw error;

      toast.success('Prescription cancelled successfully');
      setSelectedPrescription(null);
      setMedicinePrices({});
      fetchPrescriptions();
    } catch (error) {
      toast.error(`Error cancelling prescription: ${error.message}`);
    }
    setLoading(false);
  }

  async function handleLogout() {
    await logout();
    navigate('/');
  }

  // Handle global keydown for Cmd/Ctrl + Enter
  useEffect(() => {
    function handleKeyDown(e) {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        if (selectedPrescription && selectedPrescription.pharmacy_status === 'pending') {
          handleMarkAsCompleted();
        }
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedPrescription, medicinePrices, medicineUnavailable]);

  if (!pharmacyInfo) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'var(--gray-50)' }}>
        Loading Pharmacy Dashboard...
      </div>
    );
  }

  return (
    <>
      <Navbar />
      <div className="pharmacy-layout">
        <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>

        {mobileMenuOpen && (
          <div className="mobile-overlay" onClick={() => setMobileMenuOpen(false)}></div>
        )}

        <aside className={`pharmacy-sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
          <div className="sidebar-header-info">
            <div className="pharmacy-icon"><Pill size={28} color="white" /></div>
            <div className="pharmacy-details">
              <h3>Pharmacy</h3>
              <p>{pharmacyInfo?.name}</p>
            </div>
          </div>

          <nav className="sidebar-nav">
            <button
              className={`nav-item ${activeTab === 'active' ? 'active' : ''}`}
              onClick={() => { setActiveTab('active'); setMobileMenuOpen(false); setSelectedPrescription(null); }}
            >
              <ClipboardList size={20} />
              <span>Active Prescriptions</span>
              {activePrescriptions.length > 0 && (
                <span className="badge-count">{activePrescriptions.length}</span>
              )}
            </button>
            <button
              className={`nav-item ${activeTab === 'completed' ? 'active' : ''}`}
              onClick={() => { setActiveTab('completed'); setMobileMenuOpen(false); setSelectedPrescription(null); }}
            >
              <CheckCircle2 size={20} />
              <span>Completed</span>
            </button>
          </nav>

          <div className="sidebar-footer">
            <button className="logout-btn-sidebar" onClick={handleLogout}>
              <LogOut size={18} />
              <span>Logout</span>
            </button>
          </div>
        </aside>

        <main className="pharmacy-main">
          <header className="pharmacy-header">
            <div className="header-left">
              <h1>
                {activeTab === 'active' && 'Active Prescriptions'}
                {activeTab === 'completed' && 'Completed Prescriptions'}
              </h1>
              <p className="header-subtitle">
                {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            </div>
            <div className="header-search">
              <input
                type="text"
                placeholder="Search by patient name or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
          </header>

          <div className="pharmacy-content dashboard-split-view">
            <div className="master-list-pane">
              {activeTab === 'active' && (
                <div className="prescriptions-list">
                  {filteredActivePrescriptions.length === 0 ? (
                    <div className="empty-state mini">
                      <p>No active prescriptions.</p>
                    </div>
                  ) : (
                    filteredActivePrescriptions.map(prescription => (
                      <div 
                        key={prescription.id} 
                        className={`prescription-card ${selectedPrescription?.id === prescription.id ? 'active-selection' : ''}`}
                        onClick={() => handleViewPrescription(prescription)}
                      >
                        <div className="card-header" style={{ cursor: 'pointer', borderBottom: 'none', paddingBottom: 0, marginBottom: '0.5rem' }}>
                          <h4>{prescription.patient_name}</h4>
                          <span className="prescription-date">
                            {new Date(prescription.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="card-content">
                          <p>Items: {(prescription.medicines || []).length} • Dr. {prescription.doctor_name}</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {activeTab === 'completed' && (
                <div className="prescriptions-list">
                  {filteredCompletedPrescriptions.length === 0 ? (
                    <div className="empty-state mini">
                      <p>No completed prescriptions.</p>
                    </div>
                  ) : (
                    filteredCompletedPrescriptions.map(prescription => (
                      <div 
                        key={prescription.id} 
                        className={`prescription-card completed ${selectedPrescription?.id === prescription.id ? 'active-selection' : ''}`}
                        onClick={() => handleViewPrescription(prescription)}
                      >
                        <div className="card-header" style={{ cursor: 'pointer', borderBottom: 'none', paddingBottom: 0, marginBottom: '0.5rem' }}>
                          <h4>{prescription.patient_name}</h4>
                          <span className="badge-success">Dispensed</span>
                        </div>
                        <div className="card-content">
                          <p>₹{prescription.total_amount?.toFixed(2)} • {new Date(prescription.dispensed_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            <div className="detail-view-pane">
              {!selectedPrescription ? (
                <div className="empty-detail-state">
                  <ClipboardList size={64} style={{ color: '#d1d5db', margin: '0 auto 1rem' }} />
                  <h3>Select a prescription to view details</h3>
                  <p>Click on any card from the list to start processing</p>
                </div>
              ) : (
                <div className="prescription-detail-view">
                  <div className="detail-header" style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '2rem' }}>
                    <button className="btn-back mobile-only" onClick={() => setSelectedPrescription(null)} style={{ background: 'none', border: 'none', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '1rem' }}>
                      <ChevronLeft size={18} />
                      Back
                    </button>
                    <h2 style={{ margin: 0, color: '#111827' }}>Prescription Details</h2>
                  </div>

                  <div className="expanded-card-content" style={{ padding: 0, background: 'transparent', boxShadow: 'none' }}>
                    <div className="patient-quick-info">
                      <span className="badge">Age: {selectedPrescription.patient_age}</span>
                      <span className="badge">Gender: {selectedPrescription.patient_gender}</span>
                      <span className="badge">Doctor: {selectedPrescription.doctor_name}</span>
                      {selectedPrescription.diagnosis && <span className="badge highlight">Diagnosis: {selectedPrescription.diagnosis}</span>}
                    </div>

                    <div className="medicines-table expanded-table" style={{ background: 'white', borderRadius: '1rem', border: '1px solid #e5e7eb', padding: '1rem', marginBottom: '2rem' }}>
                      <div className="table-header-row">
                        <div className="col-med">Medicine</div>
                        <div className="col-qty">Instructions</div>
                        <div className="col-price">Price (₹)</div>
                        {activeTab === 'active' && <div className="col-avail">Unavail</div>}
                        <div className="col-actions"></div>
                      </div>
                      
                      {activeTab === 'active' ? (
                        (selectedPrescription.medicines || []).map((medicine, index) => (
                          <div key={index} className={`medicine-row-container ${medicineUnavailable[index] ? 'unavailable-row' : ''}`}>
                            <div className="medicine-row inline-row">
                              <div className="col-med">
                                <h4>{medicine.name}</h4>
                                <p>{medicine.dosage} • {medicine.frequency} • {medicine.duration}</p>
                              </div>
                              <div className="col-qty">
                                {medicine.instructions && <div className="med-req">{medicine.instructions}</div>}
                              </div>
                              <div className="col-price">
                                <input
                                  type="number"
                                  value={medicinePrices[index] || ''}
                                  onChange={(e) => updateMedicinePrice(index, e.target.value)}
                                  placeholder="0.00"
                                  step="0.01"
                                  min="0"
                                  disabled={medicineUnavailable[index]}
                                  tabIndex={0}
                                />
                              </div>
                              <div className="col-avail">
                                <label className="toggle-switch">
                                  <input
                                    type="checkbox"
                                    checked={medicineUnavailable[index] || false}
                                    onChange={() => toggleMedicineUnavailable(index)}
                                    tabIndex={0}
                                  />
                                  <span className="slider"></span>
                                </label>
                              </div>
                              <div className="col-actions">
                                <button
                                  type="button"
                                  className={`btn-toggle-options ${showSecondaryInputs[index] ? 'active' : ''}`}
                                  onClick={() => toggleSecondaryInputs(index)}
                                  disabled={medicineUnavailable[index]}
                                  title="More Options"
                                >
                                  ⚙️
                                </button>
                              </div>
                            </div>

                            {showSecondaryInputs[index] && !medicineUnavailable[index] && (
                              <div className="secondary-inputs-row">
                                <div className="secondary-input-group">
                                  <label>Dispensed Qty</label>
                                  <input
                                    type="text"
                                    list="qty-options"
                                    value={medicineQuantities[index] || ''}
                                    onChange={(e) => updateMedicineQuantity(index, e.target.value)}
                                    placeholder="e.g. 1 strip"
                                    tabIndex={0}
                                  />
                                </div>
                                <div className="secondary-input-group">
                                  <label>Substitute Brand</label>
                                  <input
                                    type="text"
                                    value={medicineSubstitutes[index] || ''}
                                    onChange={(e) => updateMedicineSubstitute(index, e.target.value)}
                                    placeholder="Brand name?"
                                    tabIndex={0}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        ))
                      ) : (
                        (selectedPrescription.medicines || []).map((medicine, index) => (
                          <div key={index} className={`medicine-row inline-row ${medicine.unavailable ? 'unavailable-row' : ''}`}>
                            <div className="col-med">
                              <h4>{medicine.name}</h4>
                              <p>{medicine.dosage} • {medicine.frequency}</p>
                            </div>
                            <div className="col-disp-qty val-display">
                              {medicine.unavailable ? '-' : (medicine.dispensed_quantity || 'As req')}
                            </div>
                            <div className="col-sub val-display">
                              {medicine.substituted_with || '-'}
                            </div>
                            <div className="col-price val-display">
                              {medicine.unavailable ? '₹0.00' : `₹${(medicine.price || 0).toFixed(2)}`}
                            </div>
                            <div className="col-avail val-display">
                              {medicine.unavailable ? <span className="status-badge bad">Unavail</span> : <span className="status-badge good">OK</span>}
                            </div>
                          </div>
                        ))
                      )}
                    </div>

                    {activeTab === 'active' ? (
                      <div className="expanded-actions-footer">
                        <div className="live-total-display">
                          Live Total: <span>₹{liveTotal.toFixed(2)}</span>
                        </div>
                        <div className="action-buttons">
                          <button
                            className={`btn-cancel ${cancelConfirmId === selectedPrescription.id ? 'confirm-mode' : ''}`}
                            onClick={handleCancelPrescription}
                            disabled={loading}
                          >
                            {cancelConfirmId === selectedPrescription.id ? 'Click again to confirm' : 'Cancel Prescription'}
                          </button>
                          <button className="btn-complete" onClick={handleMarkAsCompleted} disabled={loading}>
                            {loading ? 'Processing...' : 'Complete & Send Receipt (Cmd+Enter)'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="completed-total-footer">
                        <h4>Total Amount Collected:</h4>
                        <span>₹{selectedPrescription.total_amount?.toFixed(2)}</span>
                      </div>
                    )}

                    {activeTab === 'active' && (
                      <datalist id="qty-options">
                        <option value="Full Course" />
                        <option value="1 Strip" />
                        <option value="2 Strips" />
                        <option value="1 Bottle" />
                        <option value="1 Tube" />
                      </datalist>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

export default PharmacyDashboard;